import { LitElement } from '@lit'
import { t } from '@nova/utils'

class NovaVariantPicker extends LitElement {
  static properties = {
    productUrl: { type: String, attribute: 'product-url' },
    sectionId: { type: String, attribute: 'section-id' },
    variantData: { type: Object },
    currentVariant: { type: Object, state: true },
  }

  connectedCallback() {
    super.connectedCallback()
    this.productUrlChanged = false
    this._initializeElements()
    this._addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._removeEventListeners()
  }

  _initializeElements() {
    this.$optionInputs = this.querySelectorAll('.js-options-input')
    this.$colorLegend = this.querySelector('.js-color-legend')
  }

  _addEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.addEventListener('change', this._handleOptionChange)
    }

    document.addEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _removeEventListeners() {
    for (const $input of this.$optionInputs) {
      $input.removeEventListener('change', this._handleOptionChange)
    }

    document.removeEventListener(
      'nova:product:render-variant-picker',
      this._handleVariantPickerUpdate,
    )
  }

  _handleVariantPickerUpdate = (event) => {
    this.updateContent(event.detail.html)

    document.dispatchEvent(
      new CustomEvent('nova:product:render-variant-picker-finished', {
        detail: { html: event.detail.html },
      }),
    )
  }

  updateURL() {
    const searchParams = new URLSearchParams(document.location.search)
    if (this.currentVariant?.id) {
      searchParams.set('variant', this.currentVariant.id)
    }
    const url =
      searchParams.size > 0
        ? `${this.productUrl}?${searchParams.toString()}`
        : this.productUrl
    window.history.replaceState({}, '', url)
  }

  _handleOptionChange = (event) => {
    const optionType = event.target.name
    const selectedVariantId = Number.parseInt(event.target.dataset.variantId)

    this.currentVariant = structuredClone(
      this.variantData.find((variant) => variant.id === selectedVariantId),
    )

    if (optionType === 'color' && this.$colorLegend) {
      const colorOptionIndex = Number.parseInt(
        event.target.dataset.colorOptionIndex,
      )
      this.$colorLegend.textContent =
        this.currentVariant.options[colorOptionIndex]
      this.productUrlChanged = (event.target.dataset.productUrl !== this.productUrl)
      if (event.target.dataset.productUrl) {
        this.productUrl = event.target.dataset.productUrl
      }
    }

    this.updateURL()
    this._fetchProductSection()
  }

  async _fetchProductSection() {
    const response = await fetch(
      `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`,
    )
    const responseText = await response.text()
    const sectionHtml = new DOMParser().parseFromString(
      responseText,
      'text/html',
    )

    const $media = sectionHtml.querySelector('nova-product-gallery')
    if ($media) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-gallery', {
          detail: { html: $media },
        }),
      )
    }

    const $variantPicker = sectionHtml.querySelector('nova-variant-picker')
    if ($variantPicker) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-variant-picker', {
          detail: { html: $variantPicker },
        }),
      )
    }

    const $form = sectionHtml.querySelector('.js-product-form')
    if ($form) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-form', {
          detail: { html: $form },
        }),
      )
    }

    const $price = sectionHtml.querySelector('nova-price')
    if ($price) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-price', {
          detail: { html: $price },
        }),
      )
    }

    const $title = sectionHtml.querySelector('.js-product-title')
    if ($title) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-title', {
          detail: { html: $title },
        }),
      )
    }

    const $description = sectionHtml.querySelector('.js-product-description')
    if ($description) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-description', {
          detail: { html: $description },
        }),
      )
    }

    const $quantity = sectionHtml.querySelector('nova-quantity-input')
    if ($quantity) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-quantity-input', {
          detail: { html: $quantity },
        }),
      )
    }

    const $upsellProducts = sectionHtml.querySelector('.js-product-upsell')
    if ($upsellProducts) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-upsell', {
          detail: { html: $upsellProducts },
        }),
      )
    }

    const $sizeGuideContent = sectionHtml.querySelector('.js-size-guide-content')
    if ($sizeGuideContent) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-size-guide', {
          detail: { html: $sizeGuideContent },
        }),
      )
    }

    const $stickyAtbContent = sectionHtml.querySelector('.js-stick-atb')
    if ($stickyAtbContent) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-sticky-atb', {
          detail: { html: $stickyAtbContent },
        }),
      )
    }

    const $inventoryCallout = sectionHtml.querySelector('.js-inventory-callout')
    if ($inventoryCallout) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-inventory-callout', {
          detail: { html: $inventoryCallout },
        }),
      )
    }

    const $collapsibleTabs = sectionHtml.querySelectorAll('.js-update-on-variant-change')
    document.dispatchEvent(
      new CustomEvent('nova:product:render-collapsible-tabs', {
        detail: { html: $collapsibleTabs },
      }),
    )

    const $callout = sectionHtml.querySelector('.js-product-callout')
    if ($callout) {
      document.dispatchEvent(
        new CustomEvent('nova:product:render-product-callout', {
          detail: { html: $callout },
        }),
      )
    }

    if (this.productUrlChanged && window.reloadKlaviyoBIS) {
      this.productUrlChanged = false
      window.reloadKlaviyoBIS()
    }
  }

  updateContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()

    this._initializeElements()
    this._addEventListeners()
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-variant-picker', NovaVariantPicker)
